import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import gaTrackingService from '../../../services/gaTrackingService';
import { analyticsService } from '../../../services/analyticsService';
import { useAuth } from '../../../contexts/AuthContext';

const PromotionsSection = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const activePromotions = [
  {
    id: 1,
    storeName: "Ristorante La Dolce Vita",
    storeId: 1,
    category: "Ristoranti",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_102206562-1773257077360.png",
    logoAlt: "Circular restaurant logo featuring golden fork and knife crossed over red Italian flag colors on white background",
    image: "https://images.unsplash.com/photo-1610738491671-6698f7dba0ca",
    imageAlt: "Delicious Italian pasta dish with fresh tomato sauce, basil leaves, and parmesan cheese on white ceramic plate",
    title: "Sconto 20% sul menù degustazione",
    description: "Valido dal lunedì al giovedì, esclusi festivi. Prenotazione obbligatoria.",
    validUntil: "31/01/2026",
    discount: "20%",
    type: "discount"
  },
  {
    id: 2,
    storeName: "Boutique Eleganza",
    storeId: 2,
    category: "Moda & Accessori",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_15028deb0-1773257086773.png",
    logoAlt: "Sophisticated boutique logo with elegant cursive typography in gold on black circular background with decorative border",
    image: "https://images.unsplash.com/photo-1583222844068-539c9488524d",
    imageAlt: "Elegant spring fashion collection display with pastel colored dresses, accessories, and shoes arranged on modern white shelving",
    title: "Collezione Primavera -30%",
    description: "Sconto su tutta la nuova collezione primavera/estate. Non cumulabile con altre offerte.",
    validUntil: "15/02/2026",
    discount: "30%",
    type: "discount"
  },
  {
    id: 3,
    storeName: "Caffè Centrale",
    storeId: 3,
    category: "Bar & Caffè",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1c7e8f6c5-1773257072668.png",
    logoAlt: "Modern coffee shop logo with stylized coffee cup icon in brown and cream colors on circular badge",
    image: "https://images.unsplash.com/photo-1514310807142-4d5133bf3d4f",
    imageAlt: "Freshly brewed cappuccino with artistic latte art heart design in white ceramic cup on wooden table",
    title: "2x1 su colazione completa",
    description: "Ogni mattina dalle 7:00 alle 10:00. Include caffè, cornetto e spremuta.",
    validUntil: "28/02/2026",
    discount: "2x1",
    type: "offer"
  },
  {
    id: 4,
    storeName: "Libreria Moderna",
    storeId: 4,
    category: "Cultura & Tempo Libero",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1955eb0e2-1768054202892.png",
    logoAlt: "Classic bookstore logo featuring open book icon in brown with elegant serif font on cream colored circular badge",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1197d933b-1772841006762.png",
    imageAlt: "Stack of colorful bestselling books arranged on wooden table with reading glasses and warm lighting",
    title: "3x2 su libri selezionati",
    description: "Promozione valida su oltre 500 titoli. Il libro di minor valore è gratuito.",
    validUntil: "20/02/2026",
    discount: "3x2",
    type: "offer"
  }];


  const handlePromotionClick = async (storeId, promo) => {
    // Track in Supabase
    try {
      await analyticsService?.trackPromotionEngagement(storeId, 'click', user?.id || null);
    } catch (error) {
      console.error('Error tracking promotion:', error);
    }

    // Track in GA4
    gaTrackingService?.trackPromotionClick({
      promotionId: promo?.id,
      promotionTitle: promo?.title,
      storeId: storeId,
      storeName: promo?.storeName,
      discountValue: promo?.discount,
      promotionType: promo?.type,
      engagementType: 'click'
    });

    navigate(`/store-details?id=${storeId}`);
  };

  return (
    <section className="py-8 md:py-12">
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Tag" size={24} className="text-accent" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-xl md:text-2xl lg:text-3xl text-foreground">
              Promo attive oggi
            </h2>
            <p className="font-caption text-sm text-muted-foreground">
              {activePromotions?.length} promozioni disponibili
            </p>
          </div>
        </div>

        <Button
          variant="outline"
          size="sm"
          iconName="ArrowRight"
          iconPosition="right"
          onClick={() => navigate('/main-storefronts')}
          className="hidden sm:flex">

          Vedi tutte
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {activePromotions?.map((promo) =>
        <div
          key={promo?.id}
          onClick={() => handlePromotionClick(promo?.storeId, promo)}
          className="bg-card rounded-xl shadow-warm hover:shadow-warm-md transition-smooth cursor-pointer overflow-hidden group">

            <div className="relative h-40 md:h-48 overflow-hidden">
              <Image
              src={promo?.image}
              alt={promo?.imageAlt}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />

              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

              <div className="absolute top-3 right-3 bg-accent text-accent-foreground px-3 py-1.5 rounded-lg font-heading font-bold text-lg">
                {promo?.discount}
              </div>

              <div className="absolute bottom-3 left-3 right-3">
                <div className="flex items-center gap-2 bg-background/95 backdrop-blur-sm rounded-lg px-2.5 py-2">
                  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                    <Image
                    src={promo?.logo}
                    alt={promo?.logoAlt}
                    className="w-full h-full object-cover" />

                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-caption font-semibold text-xs text-foreground truncate">
                      {promo?.storeName}
                    </p>
                    <p className="font-caption text-xs text-muted-foreground truncate">
                      {promo?.category}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4">
              <h3 className="font-heading font-semibold text-base text-foreground mb-2 line-clamp-2">
                {promo?.title}
              </h3>

              <p className="font-caption text-sm text-muted-foreground mb-3 line-clamp-2">
                {promo?.description}
              </p>

              <div className="flex items-center justify-between pt-3 border-t border-border">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Icon name="Calendar" size={14} />
                  <span className="font-caption text-xs">Fino al {promo?.validUntil}</span>
                </div>

                <Icon name="ChevronRight" size={16} className="text-primary" />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="mt-6 sm:hidden">
        <Button
          variant="outline"
          size="default"
          iconName="ArrowRight"
          iconPosition="right"
          onClick={() => navigate('/main-storefronts')}
          fullWidth>

          Vedi tutte le promozioni
        </Button>
      </div>
    </section>);

};

export default PromotionsSection;